/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from 'react'
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import CloneProfileModal from './components/CloneProfileModal.jsx'
import CopyRowAsJSON from '../../components/CopyRowAsJSON.jsx'
import DebugAccordion from '../../components/DebugAccordion.jsx'
import DeleteProfileModal from './components/DeleteProfileModal.jsx'
import Icon from '../../components/Icon.jsx'
import Loadable from '../../components/Loadable.jsx'
import LoadingAnimation from '../../components/LoadingAnimation.jsx'
import NewProfileModal from './components/NewProfileModal.jsx'
import PageSizeSelector from '../../components/PageSizeSelector.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import TimestampWithUTCTooltip from '../../components/TimestampWithUTCTooltip.jsx'

import { NotificationTypes, addNotification } from '../../state/actions/notificationActions.js'
import { fetchProfileList, createProfile, deleteProfile } from '../../state/actions/profileActions.js'
import { fetchProfileSchema } from '../../state/actions/profileSchemaActions.js'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions.js'

import { profileListSignal, profileListLoadedSignal, profileListErrorSignal } from '../../state/signals/profileSignals.js'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals.js'

import { SortDirectionType } from '../../utils/constants.js'
import { computeSortedData } from '../../utils/table.js'
import { generateJsonFromSchema } from '../../utils/utils.js'

export default function ProfileList() {

    const navigate = useNavigate()
    const { beId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewProfiles) {
            addNotification('You are not authorized to view UTG profiles for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditProfiles
    })

    const profileSchemaVersionsComputed = useComputed(() => {
        if (schemaManifestSignal.value?.profiles) {
            const versions = schemaManifestSignal.value.profiles.map(p => p.utgVersion)
            return versions.sort()
        }
        return []
    })
    const defaultProfileSchemaVersionComputed = useComputed(() => {
        if (schemaManifestSignal.value?.profiles) {
            const version = schemaManifestSignal.value.profiles.find(p => p.default)
            return version
        }
    })
    useSignalEffect(() => {
        newProfileUtgVersionSignal.value = defaultProfileSchemaVersionComputed.value?.utgVersion
    })

    const handleIncludeChildAccountsToggle = (e) => {
        const checked = e.target.checked
        includeChildAccountsSignal.value = checked
        fetchProfileList(beId, config, accessToken, checked)
    }

    useEffect(() => {
        fetchProfileList(beId, config, accessToken, includeChildAccountsSignal.value)
        fetchSchemaManifest()/*.then(
            (manifest) => {
                fetchProfileSchema(beId, null, manifest, config, accessToken)
            }
        )*/
    }, [beId, config, accessToken])

    // Action toolbar
    /*const handleToolSelect = item => {
        if (item == 'new') {
            showModalNewSignal.value = true
        }
        if (item == 'about') {
            console.log('not implemented yet')
        }
    }*/

    // Clone profile modal
    const showModalCloneSignal = useSignal(false)
    const cloneProfileItemSignal = useSignal({})
    
    const handleCloneProfileShowModal = item => {
        if (item) {
            cloneProfileItemSignal.value = item
            showModalCloneSignal.value = true
        }
    }
    const handleSubmitModalClone = async () => {
        showModalCloneSignal.value = false
        const profileConfig = {
            "name": newProfileNameSignal.value,
            "businessEntityId": beId,
            "type": 1,
            "config": cloneProfileItemSignal.value.config
        }
        createProfile(beId, profileConfig, config, accessToken, true).then(
            () => {
                newProfileNameSignal.value = ''
                addNotification('Profile successfully cloned', 'success', true)
            }
        ).catch(
            (error) => {
                addNotification(`Unable to clone profile. ${error}`)
            }
        )
    }

    // Delete profile modal
    const showModalDeleteSignal = useSignal(false)
    const deleteProfileItemSignal = useSignal({})
    const handleDeleteProfileShowModal = item => {
        if (item) {
            deleteProfileItemSignal.value = item
            showModalDeleteSignal.value = true
        }
    }
    const handleSubmitModalDelete = async () => {
        showModalDeleteSignal.value = false
        deleteProfile(beId, deleteProfileItemSignal.value.id, deleteProfileItemSignal.value.name, config, accessToken).then(
            () => {
                addNotification('Profile successfully deleted', 'success', true)
                fetchProfileList(beId, config, accessToken, includeChildAccountsSignal.value)
            }
        ).catch(
            (error) => {
                addNotification(`Unable to delete profile. ${error}`)
            }
        )
    }

    // New profile modal
    const showModalNewSignal = useSignal(false)
    const newProfileNameSignal = useSignal('') // server enforces uniqueness within be
    const profileNamesComputed = useComputed(() => {
        if (profileListSignal.value) {
            return profileListSignal.value.map((p) => p.name)
        }
        return []
    })
    const nameAlreadyUsedComputed = useComputed(() => {
        return profileNamesComputed.value.length && profileNamesComputed.value.includes(newProfileNameSignal.value)
    })
    const newProfileUtgVersionSignal = useSignal('')

    const handleSubmitModalNew = async () => {
        showModalNewSignal.value = false
        profileListLoadedSignal.value = true
        // load the specified schema, generate default profile from it and navigate to edit profile pages
        fetchProfileSchema(beId, newProfileUtgVersionSignal.value, schemaManifestSignal.value, config, accessToken).then(
            async (schema) => {
                if (typeof schema == 'undefined') {
                    throw new Error("fetchData returned nothing")
                }
                const jsonProfile = generateJsonFromSchema(schema)

                // add the schema verson to the config for easy tracking
                jsonProfile.config.utgGlobal.ccschema = newProfileUtgVersionSignal
                const profileConfig = {
                    "name": newProfileNameSignal.value,
                    "businessEntityId": beId,
                    "type": 1,
                    "config": jsonProfile.config
                }
                createProfile(beId, profileConfig, config, accessToken, true).then(
                    () => {
                        newProfileNameSignal.value = ''
                        addNotification('Profile successfully created', 'success', true)
                        //fetchProfileList(beId, config, accessToken)
                    }
                ).catch(
                    (error) => {
                        addNotification(`Unable to create profile. ${error}`)
                    }
                )
            }
        ).catch(
            (error) => {
                addNotification(`Unable to create profile: Error fetchinf profile list. ${error}`)
            }
        )
    }

    // Table
    const currentPageSignal = useSignal(1)
    const rowsPerPageSignal = useSignal(parseInt(window.localStorage.getItem('pagesize')) || 250)
    const filterStringSignal = useSignal("")
    const sortColumnSignal = useSignal('')
    const includeChildAccountsSignal = useSignal(false)

    const sortDirectionSignal = useSignal(SortDirectionType.DESCENDING)

    useSignalEffect(() => {
        window.localStorage.setItem('pagesize', rowsPerPageSignal.value)
    })

    const filteredDataComputed = useComputed(() => {
        if (!filterStringSignal.value) {
            return profileListSignal.value ? [...profileListSignal.value] : []
        }

        // Convert the filter to lowercase
        const lowerCaseFilter = filterStringSignal.value.toLowerCase()

        // Filter the data based on the filter text
        return profileListSignal.value.filter(item => {

            // Limit search to these fields
            let searchData = {
                name: item.name,
                businessEntityName: item.businessEntitySummary.name,
                version: item.version,
                schema: item.config?.utgGlobal?.ccschema
            }

            return Object.values(searchData).some(value => {
                if (value && (typeof (value) == 'string')) {
                    return value.toString().toLowerCase().includes(lowerCaseFilter)
                }
            })
        })
    })

    const sortedDataComputed = useComputed(computeSortedData(filteredDataComputed, sortColumnSignal, sortDirectionSignal))

    const totalPagesComputed = useComputed(() => {
        return Math.ceil(filteredDataComputed.value.length / parseInt(rowsPerPageSignal.value, 10))
    })

    const displayDataComputed = useComputed(() => {
        // Calculate the starting and ending indexes for pagination
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value

        return sortedDataComputed.value.slice(startIndex, endIndex)
    })
    const startDisplayRowNumberComputed = useComputed(() => {
        return ((currentPageSignal.value - 1) * rowsPerPageSignal.value) + 1
    })
    const endDisplayRowNumberComputed = useComputed(() => {
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return endIndex < filteredDataComputed.value.length ? endIndex : filteredDataComputed.value.length
    })

    const accordionData = [
        { name: 'Profiles', data: profileListSignal.value },
        { name: 'Display Data', data: displayDataComputed.value },
        { name: 'Sorted Data', data: sortedDataComputed.value },
        { name: 'Filtered Data', data: filteredDataComputed.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: schemaManifestSignal.value },
        { name: 'Profile Schema Versions', data: profileSchemaVersionsComputed.value }
    ]

    const handlePageSizeSelect = (v) => {
        currentPageSignal.value = 1
        rowsPerPageSignal.value = parseInt(v, 10)
    }
    const handleFilterStringChange = (e) => {
        currentPageSignal.value = 1
        filterStringSignal.value = e.currentTarget.value
    }

    const handleHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == sortColumnSignal.value) {
            sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        sortColumnSignal.value = datafield
    }

    function TableRows({ items }) {
        if (!profileListLoadedSignal.value) {
            return <tr><td colSpan={9}><LoadingAnimation /></td></tr>
        }
        if (items && items.length) {
            const rows = items.map(
                item => {
                    return <TableRow item={item} key={`${item.id}`} />
                }
            )
            return rows
        }
        return <tr><td colSpan={9}>The selected business entity has no profiles.</td></tr>
    }

    function TableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions"><Icon icon="gear" width={16} height={16}></Icon></Dropdown.Toggle>
                        <Dropdown.Menu>
                            <CopyRowAsJSON item={item} field="name" />
                            {canEditComputed.value && <Dropdown.Item onClick={() => handleDeleteProfileShowModal(item)}><Icon icon="trash" width={16} height={16}></Icon> Delete profile</Dropdown.Item>}
                            {canEditComputed.value && <Dropdown.Item onClick={() => handleCloneProfileShowModal(item)}><Icon icon="copy" width={16} height={16}></Icon> Clone profile</Dropdown.Item>}
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td><Link to={`../../be/${item.businessEntityId}/profiles/${item.id}`}>{item.name}</Link></td>
                <td>{(item.businessEntitySummary && item.businessEntitySummary.name) ? item.businessEntitySummary.name : ''}</td>
                <td>{item.version}</td>
                <td><TimestampWithUTCTooltip>{item.createdAt}</TimestampWithUTCTooltip></td>
                <td><TimestampWithUTCTooltip>{item.lastUpdatedAt}</TimestampWithUTCTooltip></td>
                <td>{(item.config.utgGlobal && item.config.utgGlobal.ccschema) ? item.config.utgGlobal.ccschema : ''}</td>
                <td>{item.config?.deviceConfig?.device?.length}</td>
            </tr>
        )
    }

    function PaginationItems() {
        let items = []
        for (let i = 1; i <= totalPagesComputed.value; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => currentPageSignal.value = i}>{i}</Pagination.Item>
            )
        }
        return items
    }

    function TableColumnHeader({ title, sortable, datafield }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumnSignal.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width={16} height={16}></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width={16} height={16}></Icon>
            </th>
        )
    }

    return (
        <>

            <Stack direction="horizontal" gap={3}>
                <PageTitle title='Profile List' canEditSignal={canEditComputed} />
            </Stack>
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <Nav.Item className="me-auto">
                    <PageSizeSelector pageSizeSignal={rowsPerPageSignal} handlePageSizeSelect={handlePageSizeSelect} />
                </Nav.Item>
                <Nav.Item className="d-flex align-items-center me-3">
                    <Form.Check
                        type="switch"
                        id="include-child-accounts-switch"
                        label="Include Child Accounts"
                        checked={includeChildAccountsSignal.value}
                        onChange={handleIncludeChildAccountsToggle}
                    />
                </Nav.Item>
                <Nav.Item>
                    <Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" onChange={handleFilterStringChange} />
                </Nav.Item>
                {canEditComputed.value && <Nav.Item className="ms-2">
                    <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={() => showModalNewSignal.value = true}><Icon icon='plus-circle' width="16" height="16" ></Icon>&nbsp;New</Button>
                </Nav.Item>}
            </Nav>
            <Table responsive striped hover size="sm" className="cc-table">
                <thead>
                    <tr>
                        <TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
                        <TableColumnHeader title={'Profile Name'} sortable={true} datafield={'name'}></TableColumnHeader>
                        <TableColumnHeader title={'Parent Account'} sortable={true} datafield={'businessEntitySummary.name'}></TableColumnHeader>
                        <TableColumnHeader title={'Profile Version'} sortable={true} datafield={'version'}></TableColumnHeader>
                        <TableColumnHeader title={'Creation Date'} sortable={true} datafield={'createdAt'}></TableColumnHeader>
                        <TableColumnHeader title={'Last Updated'} sortable={true} datafield={'lastUpdatedAt'}></TableColumnHeader>
                        <TableColumnHeader title={'Schema'} sortable={true} datafield={'config.utgGlobal.ccschema'}></TableColumnHeader>
                        <TableColumnHeader title={'Devices'} sortable={false}></TableColumnHeader>
                    </tr>
                </thead>
                <tbody><TableRows items={displayDataComputed.value} /></tbody>
                <tfoot><tr><td className="fs-6" colSpan={19}>{
                    filteredDataComputed.value.length > 0 && (
                        <>Displaying {startDisplayRowNumberComputed.value}-{endDisplayRowNumberComputed.value} of {filteredDataComputed.value.length}</>
                    )
                }</td></tr></tfoot>
            </Table>
            <Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
            <DebugAccordion data={accordionData} debug={debug} />

            <NewProfileModal
                showSignal={showModalNewSignal}
                newProfileNameSignal={newProfileNameSignal}
                newProfileUtgVersionSignal={newProfileUtgVersionSignal}
                nameAlreadyUsedComputed={nameAlreadyUsedComputed}
                profileSchemaVersionsComputed={profileSchemaVersionsComputed}
                handleSubmit={handleSubmitModalNew}
            ></NewProfileModal>

            <DeleteProfileModal
                showSignal={showModalDeleteSignal}
                deleteProfileItemSignal={deleteProfileItemSignal}
                newProfileNameSignal={newProfileNameSignal}
                handleSubmit={handleSubmitModalDelete}
            ></DeleteProfileModal>

            <CloneProfileModal
                showSignal={showModalCloneSignal}
                cloneProfileItemSignal={cloneProfileItemSignal}
                newProfileNameSignal={newProfileNameSignal}
                nameAlreadyUsedComputed={nameAlreadyUsedComputed}
                handleSubmit={handleSubmitModalClone}
            ></CloneProfileModal>

        </>
    )
}